import { useState, useCallback, useEffect } from 'react';
import { FormikHelpers } from 'formik';
import get from 'lodash/get';
// import remove from 'lodash/remove';
import md5 from 'js-md5';
import { ENDPOINT, useConfig } from '@server/lib/config';

function isImageFileType(type) {
  return !!type && type.indexOf('image/') === 0;
}

export function extname(url) {
  // const url = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  const temp = url.split('/');
  const filename = temp[temp.length - 1];
  const filenameWithoutSuffix = filename.split(/#|\?/)[0];
  return (/\.[^./\\]*$/.exec(filenameWithoutSuffix) || [''])[0];
}

function isImageUrl(file: any) {
  if (isImageFileType(file.type)) {
    return true;
  }

  var url = file.thumbUrl || file.url;
  var extension = extname(url);

  if (/^data:image\//.test(url) || /(png|gif|jpg|jpeg)$/i.test(extension)) {
    return true;
  }

  if (/^data:/.test(url)) {
    // other file types of base64
    return false;
  }

  if (extension) {
    // other file types which have extension
    return false;
  }

  return true;
}

function readAsArrayBuffer(file: File): Promise<any> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = function (event) {
      resolve(event.target.result);
    };
    fr.readAsArrayBuffer(file);
  });
}

async function handleUploadFile(file: File, secretKey: string): Promise<any> {
  const bodyFile = await readAsArrayBuffer(file);
  const hashFile = md5.create().update(bodyFile).hex();
  const checksum = md5.create().update(`${hashFile}${secretKey}`).hex();

  let formData = new FormData();
  formData.append('file', file);
  formData.append('checksum', checksum);

  const response = await window.fetch(`${ENDPOINT}/v2/web/upload`, {
    method: 'POST',
    mode: 'cors',
    body: formData,
  });

  if (response.ok) {
    const json = await response.json();
    return Promise.resolve(json);
  }

  return Promise.reject('Upload:' + checksum + JSON.stringify(response));
}

interface RequestPayload {
  name: string;
  phone: string;
  email: string;
  files: string[];
}

async function handleRequest(payload: RequestPayload): Promise<any> {
  const response = await window.fetch(`${ENDPOINT}/v2/web/request_quote`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      customer_name: payload.name,
      customer_phone: payload.phone,
      customer_email: payload.email,
      files: payload.files,
      channel_id: 5,
    }),
  });

  if (response.ok) {
    const json = await response.json();
    const message = get(json, 'message');
    if (!!message) {
      return Promise.reject(message);
    }

    return Promise.resolve(json);
  }

  return Promise.reject(response);
}

/**
 * Maximum allowed size in bytes
 * 5MB Example
 * Change first operand(multiplier) for your needs
 */
const MAX_ALLOWED_SIZE = 5 * 1024 * 1024;
export const MAX_FILE_UPLOAD = 3;
export const useUpload = () => {
  const { SECRET_KEY_UPLOAD } = useConfig();

  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [error, setError] = useState<string>();
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  // useEffect(() => {
  //   window.addEventListener('beforeunload', alertUser);
  //   window.addEventListener('unload', alertUser);
  //   return () => {
  //     window.removeEventListener('beforeunload', alertUser);
  //     window.removeEventListener('unload', alertUser);
  //   };
  // });

  // // const handleTabClosing = useCallback(() => {
  // //   // removePlayerFromGame()
  // // }, [loading]);

  // const alertUser = useCallback(
  //   (event: any) => {
  //     event.preventDefault();
  //     event.returnValue = '';
  //   },
  //   [loading]
  // );

  const handleToggleDialog = useCallback(() => {
    setOpenDialog(!openDialog);
  }, [openDialog, setOpenDialog]);

  const handleFileChanged = useCallback(
    async (event) => {
      event.preventDefault();
      setError(undefined);

      const _files = [...files];

      const _fileList = (event.target as HTMLInputElement).files;
      const currentLength = _files.length;
      const nextLength = _fileList.length;
      const maxLength = currentLength + nextLength;
      if (maxLength > MAX_FILE_UPLOAD) {
        setError('Chỉ tải lên tối đa được 3 ảnh');
        return;
      }

      for (let index = 0; index < nextLength; index++) {
        const file = _fileList[index];
        // if (!isImageUrl(file)) {
        //   setError('Vui lòng tải hình ảnh');
        //   return;
        // }

        if (file.size > MAX_ALLOWED_SIZE) {
          setError('Dung lượng tối đa tải lên là 2MB');
          return;
        }

        if (file.size > MAX_ALLOWED_SIZE) {
          setError('Dung lượng tối đa tải lên là 2MB');
          return;
        }

        _files.push(file);
      }

      setFiles(_files);
      setError(undefined);
    },
    [files, setFiles, setError]
  );

  const handleSubmit = useCallback(
    async (
      values: {
        name: string;
        phone: string;
        email: string;
      },
      formikHelpers: FormikHelpers<{
        name: string;
        phone: string;
        email: string;
      }>
    ) => {
      try {
        setLoading(true);
        formikHelpers.setSubmitting(true);
        if (files.length == 0) {
          setError('Vui lòng chọn ít nhất 1 ảnh');
          formikHelpers.setSubmitting(false);
          setLoading(false);

          return;
        }

        if (files.length > MAX_FILE_UPLOAD) {
          setError('Chỉ tải lên tối đa được 3 ảnh');
          return;
        }

        const filesResult = await Promise.all(
          files.map((file) => handleUploadFile(file, SECRET_KEY_UPLOAD))
        );

        await handleRequest({
          ...values,
          files: filesResult.map((file) => get(file, 'url')),
        });

        setError(undefined);
        formikHelpers.resetForm();
        formikHelpers.setSubmitting(false);
        setLoading(false);
        setFiles([]);
        handleToggleDialog();
      } catch (ex) {
        console.log(ex);
        setError(JSON.stringify(ex));
        formikHelpers.setSubmitting(false);
        setLoading(false);
      }
    },
    [
      files,
      loading,
      error,
      setLoading,
      setError,
      setFiles,
      handleUploadFile,
      handleToggleDialog,
    ]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      files.splice(index, 1);
      setFiles([...files]);
      setError(undefined);
    },
    [files, setFiles, setError]
  );

  return {
    loading,
    error,
    files,
    openDialog,
    handleFileChanged,
    handleSubmit,
    handleRemoveFile,
    handleToggleDialog,
  };
};
